<script setup>
import { getDoc, doc } from "firebase/firestore";
import db from "../main.js";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import DOMPurify from "dompurify";

const orderDetails = ref({});

const route = useRoute();

const orderNumber = ref();
orderNumber.value = route.query.orderNumber;
const status = ref();
const totalPrice = ref();
const vendorNameja = ref();
const createdAt = ref();
const projectName = ref("");

const getOrderdetail = async () => {
  const docRef = doc(db, "Crowdfunding_orders", orderNumber.value);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    orderDetails.value = docSnap.data();
    status.value = docSnap.data().status;
    totalPrice.value = docSnap.data().total_price;
    vendorNameja.value = docSnap.data().vendorname_ja;
    const timestamp = docSnap.data().created_at;
    if (timestamp) {
      const date = new Date(timestamp.seconds * 1000);
      createdAt.value = date.toLocaleDateString();
    }

    // プロジェクト名を取得
    const eventId = docSnap.data().event_id;
    if (eventId) {
      try {
        const projectDocRef = doc(db, "Crowdfunding_projects", eventId);
        const projectDocSnap = await getDoc(projectDocRef);
        if (projectDocSnap.exists()) {
          projectName.value = projectDocSnap.data().projectTitle || "";
        }
      } catch (error) {
        console.error("プロジェクト名の取得エラー:", error);
      }
    }
  }
};

getOrderdetail();

const returnDetails = ref({});
const returnProducts = ref([]);

const getReturndetails = async () => {
  const docRef = doc(db, "Crowdfunding_orders", orderNumber.value, "orderreturns", "returns");
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    returnDetails.value = docSnap.data();
    returnProducts.value = docSnap.data().products || [];
  }
};

getReturndetails();

// HTMLをサニタイズする関数
const sanitizeHtml = (html) => {
  if (!html) return "";
  // 基本的なHTMLタグを許可（br, p, strong, em, ul, ol, li, aなど）
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["br", "p", "strong", "em", "u", "b", "i", "ul", "ol", "li", "a", "span", "div"],
    ALLOWED_ATTR: ["href", "target", "rel", "style"],
  });
};

const router = useRouter();
const toLink = (url) => {
  router.push(url);
};
</script>

<template>
  <v-app>
    <div class="maincontainer">
      <div class="title-container">
        <h2 class="title">配信クラファン応援支援詳細</h2>
      </div>
      <div class="details-container">
        <p>
          注文番号：<span class="ordernumbertext">{{ orderNumber }}</span>
        </p>
        <p>応援支援日：{{ createdAt }}</p>
        <p>プロジェクト名：{{ projectName }}</p>
        <p>請求金額：{{ totalPrice }}円</p>
        <p>配信者名：{{ vendorNameja }}</p>
      </div>
    </div>
    <template v-if="returnProducts.length > 0">
      <div
        v-for="(product, pIndex) in returnProducts"
        :key="'product-' + pIndex"
        class="product-container"
      >
        <div class="product-details">
          <div class="product-info">
            <div class="info-group">
              <div class="labels"><span class="underline">リターン名:</span></div>
              <div class="values">{{ product.title || product.return_name }}</div>
            </div>
            <div class="info-group description-group">
              <div class="labels"><span class="underline">説明:</span></div>
              <div class="values description-text" v-html="sanitizeHtml(product.description)"></div>
            </div>
            <div class="info-group">
              <div class="labels"><span class="underline">応援支援金額:</span></div>
              <div class="values">{{ product.price }}円</div>
            </div>
          </div>
          <img
            v-if="product.return_image"
            :src="product.return_image"
            class="product-image"
            alt="Return Image"
          />
        </div>
        <hr v-if="pIndex < returnProducts.length - 1" class="product-divider" />
      </div>
    </template>
    <div class="returncontainer" @click="toLink('orderhistorycrowdfunding')">
      <v-btn variant="outlined" height="30" width="65" rounded="pill" color="black" class="btn">
        <a class="return">戻る</a>
      </v-btn>
    </div>
  </v-app>
</template>

<style scoped>
.title {
  line-height: 1.3;
}
.title-container {
  margin: 50px 50px;
}

.details-container {
  text-align: left;
  margin-left: 40px;
}

.product-container {
  border: 1px solid #000;
  padding: 20px;
  margin: 20px;
  border-radius: 15px;
  padding-bottom: 40px;
}

.product-details {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  margin-bottom: 20px;
}

.product-info {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.info-group {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 10px;
}

.description-group {
  flex-direction: column;
  align-items: flex-start;
}

.description-text {
  text-align: left;
  width: 100%;
}

.labels {
  margin-right: 10px;
}

hr {
  border-top: 1px solid #000;
  margin-bottom: 20px;
}

.underline {
  white-space: nowrap;
  text-decoration: underline;
}

.product-image {
  width: 100px;
  height: auto;
}

.product-divider {
  border-top: 1px solid #000;
  margin: 20px 0;
}

.returncontainer {
  margin: 30px 0 30px 0;
}

.btn {
  padding: 15px 20px !important;
  font-size: 0.9rem !important;
  border-radius: 100px !important;
  border: none !important;
  margin: 0 5px !important;
  background: rgb(226, 226, 226) !important;
  color: rgb(142, 144, 147) !important;
  box-shadow: none !important;
  min-width: auto !important;
  width: auto !important;
  height: auto !important;
}

.btn:active,
.btn:hover {
  background: rgb(190, 190, 190) !important;
  color: rgb(255, 255, 255) !important;
}

.return {
  color: rgb(142, 144, 147) !important;
  font-size: 0.9rem !important;
  text-decoration: none !important;
}

.ordernumbertext {
  font-size: 14px;
}
</style>
